import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { FormControl } from '@angular/forms'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { combinedNouns, salutations } from '@app/v2/shared/types/salutations.type'
import { FullnameValidator } from '@app/v2/shared/validators/fullname.validator'
import { Subject, takeUntil } from 'rxjs'
import { BasicInputComponent } from '../basic-input/basic-input.component'

@Component({
  selector: 'fullname',
  templateUrl: './fullname.component.html',
  styleUrls: [],
  standalone: true,
  imports: [BasicInputComponent]
})
export class FullnameComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() control: FormControl = new FormControl()
  @Input() label: string = 'Your full name'
  @Input() id: string = 'fullname-input'
  @Input() set showIcon(value: boolean) {
    this.icon = value
      ? `<svg class='tw-w-6 tw-h-6 tw-text-gray-800 dark:tw-text-white' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 14 18'>
    <path d='M7 9a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm2 1H5a5.006 5.006 0 0 0-5 5v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2a5.006 5.006 0 0 0-5-5Z'/>
  </svg>`
      : ''
  }
  @Input() required: boolean = false
  @Input() showSegments: boolean = true
  @Input() mode?: BasicInputMode
  @Input() allowClearButton: boolean = true
  @Input() autofocus: boolean = false
  @Output() namePartsEmitter = new EventEmitter<Record<string, string | null>>()
  @ViewChild(BasicInputComponent) basicInput!: BasicInputComponent
  icon!: string

  salutationsSet = new Set(salutations)
  combinedNounsSet = new Set(combinedNouns)
  public basicFieldType = BasicFieldType.TEXT
  public fullnameParts: { fullnameSegments: string[]; processString: Record<string, string | null> } = {
    fullnameSegments: [],
    processString: {}
  }

  private _destroy$ = new Subject<void>()
  private _lastCursorPosition: number = 0
  private readonly _boundOnKeydown = this._onKeydown.bind(this)
  private readonly _boundOnFocus = this._onFocus.bind(this)

  constructor(private _changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.control.addValidators([FullnameValidator])

    // Initialize fullnameParts if there's already a value, otherwise clear them
    if (this.control.value && this.control.value.trim() !== '') {
      const nameParts = this.processString(this.control.value)
      this._updateFullnameParts(nameParts)
    }

    this.control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((currentValue: string) => {
      const cursorPosition = this._lastCursorPosition

      // Backup original value for comparison after transformation
      let originalValue = currentValue

      // Salutation detection algorithm
      const salutation = this.separateSalutation(currentValue).replace(/\./g, '').toLowerCase()

      let formattedValue = currentValue
        .replace(/\./g, '')
        ?.split(' ')
        .map((w, i) => {
          if (i >= salutation?.split(' ').length) return w
          if (!currentValue) return
          if (this.salutationsSet.has(w.toLowerCase())) return w + '.'
          else return w
        })
        .join(' ')

      const capitalizedFirstLetterRegex = /\b[a-z](?!\s)/g
      const nonLettersRegex = /[^a-zA-Z\u00C0-\u00FF\u0621-\u064A\.\s]/g
      const moreThanOneSpaceRegex = / {2,}/g
      const moreThanOneDotRegex = /\.{2,}/g
      formattedValue = formattedValue
        .replace(nonLettersRegex, '')
        .replace(moreThanOneSpaceRegex, ' ')
        .replace(moreThanOneDotRegex, '.')
        .replace(capitalizedFirstLetterRegex, (letter) => letter.toUpperCase())
      formattedValue = formattedValue.startsWith(' ') ? formattedValue.trim() : formattedValue

      // Only update if formatting changed something
      if (formattedValue !== originalValue) {
        let adjustedCursorPosition = cursorPosition

        // If we're adding characters (like periods), adjust cursor position
        if (formattedValue.length > originalValue.length) {
          // Special handling for salutation dots - cursor should be after the dot
          if (formattedValue.includes('.') && !originalValue.includes('.')) {
            // Find the position of the added dot
            const dotIndex = formattedValue.indexOf('.')
            // Always position cursor AFTER the dot for salutations
            if (dotIndex !== -1) adjustedCursorPosition = dotIndex + 1
          } else {
            const lengthDiff = formattedValue.length - originalValue.length
            if (lengthDiff > 0) adjustedCursorPosition += lengthDiff
          }
        }

        // Update the value without emitting the event to prevent infinite loops
        this.control.setValue(formattedValue, { emitEvent: false })

        // Restore cursor position after a short delay to ensure DOM is updated
        setTimeout(() => {
          const inputElement = this.basicInput?.inputElement?.nativeElement
          if (inputElement && document.activeElement === inputElement) {
            // Position cursor AFTER the character that was just typed
            let finalPosition = cursorPosition

            // If formatting changed the text, use adjusted position
            if (adjustedCursorPosition !== cursorPosition) finalPosition = Math.min(adjustedCursorPosition, formattedValue.length)
            else finalPosition = cursorPosition + 1

            // Ensure cursor position is within bounds
            finalPosition = Math.min(finalPosition, formattedValue.length)
            finalPosition = Math.max(finalPosition, 0)

            inputElement.setSelectionRange(finalPosition, finalPosition)
          }
        }, 0)
      }

      const nameParts = this.processString(formattedValue || currentValue)
      this.namePartsEmitter.emit(nameParts)

      // Update fullnameParts for segments display
      this._updateFullnameParts(nameParts)
    })
  }

  /**
   * Clears the fullnameParts segments
   */
  public clearFullnameParts(): void {
    // Force Angular to detect the change by creating a new object
    this.fullnameParts = {
      fullnameSegments: [],
      processString: {}
    }

    // Emit empty data to clear any parent component state
    this.namePartsEmitter.emit({
      salutations: null,
      firstName: null,
      middleName: null,
      lastName: null
    })

    // Force change detection
    this._changeDetectorRef.detectChanges()
  }

  /**
   * Updates the fullnameParts property for segment display
   */
  private _updateFullnameParts(nameParts: Record<string, string | null>): void {
    const currentValue = this.control.value || ''

    // Define segment configuration with conditions and value extractors
    const segmentConfig = [
      {
        key: 'salutations',
        condition: () => !!nameParts.salutations,
        value: () => nameParts.salutations
      },
      {
        key: 'first_name',
        condition: () => !!nameParts.firstName || this._hasFirstNameContent(currentValue, nameParts.salutations),
        value: () => nameParts.firstName || this._extractFirstName(currentValue, nameParts.salutations)
      },
      {
        key: 'middle_name',
        condition: () => !!nameParts.middleName,
        value: () => nameParts.middleName
      },
      {
        key: 'last_name',
        condition: () => !!nameParts.lastName,
        value: () => nameParts.lastName
      }
    ]

    // Build segments and processString dynamically
    const segments: string[] = []
    const processString: Record<string, string | null> = {}

    segmentConfig.forEach((config) => {
      if (config.condition()) {
        segments.push(config.key)
        processString[config.key] = config.value()
      }
    })

    this.fullnameParts = {
      fullnameSegments: segments,
      processString: processString
    }
  }

  /**
   * @description Checks if there's first name content being typed
   * @param  {string} value
   * @param  {string | null} salutations
   * @returns {boolean}
   */
  private _hasFirstNameContent(value: string, salutations: string | null): boolean {
    if (!value) return false

    const words = value.trim().split(' ')
    if (salutations) {
      const salutationWords = salutations.split(' ').length
      // If there are words after salutations, consider it first name content
      return words.length > salutationWords
    }

    // If no salutations, any word is considered first name content
    return words.length > 0
  }

  /**
   * @description Extracts the first name from current input
   * @param  {string} value
   * @param  {string | null} salutations
   * @returns {string}
   */
  private _extractFirstName(value: string, salutations: string | null): string {
    if (!value) return ''

    const words = value.trim().split(' ')
    if (salutations) {
      const salutationWords = salutations.split(' ').length
      return words.slice(salutationWords).join(' ')
    }
    return words[0] || ''
  }

  ngAfterViewInit(): void {
    // Set up input event listener for cursor position tracking
    if (this.basicInput) {
      const inputElement = this.basicInput.inputElement?.nativeElement
      if (inputElement) {
        // Track cursor position BEFORE any changes (on keydown)
        inputElement.addEventListener('keydown', this._boundOnKeydown)

        // Also track on focus to get initial position
        inputElement.addEventListener('focus', this._boundOnFocus)
      }
    }
  }

  /**
   * Track cursor position on focus
   */
  private _onFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement
    if (inputElement) {
      this._lastCursorPosition = inputElement.selectionStart || 0
    }
  }

  /**
   * Track cursor position on keydown (BEFORE typing)
   */
  private _onKeydown(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement
    if (inputElement) {
      // Capture cursor position BEFORE the key is processed
      this._lastCursorPosition = inputElement.selectionStart || 0
    }
  }

  /**
   * @method deleteChar
   * @description Handles backspace for salutation deletion with proper cursor positioning
   * @param {KeyboardEvent} e - The KeyboardEvent object
   */
  public deleteChar(e: KeyboardEvent): void {
    // Only handle backspace events
    if (e.code !== 'Backspace' && e.key !== 'Backspace') return

    const value: string = this.control.value
    const cursorPosition = (e.target as HTMLInputElement).selectionStart || 0

    // Handle end-of-input salutation deletion (e.g., "Dr." at the end)
    if (value.slice(-1) === '.') {
      e.preventDefault()
      const newValue = value.slice(0, -2)
      this.control.setValue(newValue, { emitEvent: false })

      // Update segments immediately after salutation deletion
      const nameParts = this.processString(newValue)
      this.namePartsEmitter.emit(nameParts)
      this._updateFullnameParts(nameParts)

      setTimeout(() => {
        const inputElement = e.target as HTMLInputElement
        if (inputElement) {
          const newPosition = Math.max(0, cursorPosition - 2)
          inputElement.setSelectionRange(newPosition, newPosition)
        }
      }, 0)
      return
    }

    const beforeCursor = value.substring(0, cursorPosition)
    const afterCursor = value.substring(cursorPosition)

    // Get the last word before cursor (trimmed to avoid empty strings)
    const words = beforeCursor
      .trimEnd()
      .split(' ')
      .filter((word) => word.length > 0)
    const lastWord = words[words.length - 1]

    // Check if the last word is a salutation
    if (lastWord && this.salutationsSet.has(lastWord.toLowerCase().replace(/\.$/, ''))) {
      e.preventDefault()

      // Remove the salutation and clean up spaces
      const salutationStart = beforeCursor.lastIndexOf(lastWord)
      const newValue = (beforeCursor.substring(0, salutationStart) + afterCursor).replace(/\s{2,}/g, ' ').trim()

      this.control.setValue(newValue, { emitEvent: false })

      // Update segments immediately after salutation deletion
      const nameParts = this.processString(newValue)
      this.namePartsEmitter.emit(nameParts)
      this._updateFullnameParts(nameParts)

      // Position cursor where the salutation was
      setTimeout(() => {
        const inputElement = e.target as HTMLInputElement
        if (inputElement) inputElement.setSelectionRange(salutationStart, salutationStart)
      }, 0)
    }
  }

  /**
   * @method formatBlur
   * @description Trims the control value and applies proper capitalization when the input loses focus.
   */
  formatBlur() {
    this.control.setValue(this.control.value?.trim(), { emitEvent: true })
  }

  /**
   * @description Separates the salutation from the full name.
   * @param {string} value - The input string.
   * @returns {string} The salutation string.
   */
  separateSalutation(value: string): string {
    if (typeof value !== 'string') return ''
    const valueArray = value.split(' ')
    const salutation = []
    for (let i = 0; i < valueArray.length; i++) {
      if (!this.salutationsSet.has(valueArray[i].toLowerCase().replace(/\./g, ''))) break

      salutation.push(valueArray[i][0].toUpperCase() + valueArray[i].slice(1))
    }
    return salutation.map((w) => w.replace(/\./g, '') + '.').join(' ')
  }

  /**
   * @method processString
   * @description Processes the control value, breaking it down into salutation, first name, middle name, last name, and combined noun.
   * @param {string} str - The input string.
   * @returns Record<string, string | null> An object containing the salutation, first name, middle name, last name, and combined noun.
   */
  processString(str: string): Record<string, string | null> {
    let words = str.split(' ')
    let salutationArray: string[] = []
    let firstName = ''
    let lastName = ''
    let middleName = ''

    // Separate salutations
    words = words.filter((word) => {
      let wordLower = word.toLowerCase()
      if (wordLower.endsWith('.') && this.salutationsSet.has(wordLower.slice(0, -1))) {
        salutationArray.push(word)
        return false
      }
      return true
    })

    // Merge words that are preceded by an element of the COMBINEDNOUNS array
    let result = []
    for (let i = 0; i < words.length; i++) {
      if (this.combinedNounsSet.has(words[i].toLocaleLowerCase()) && i < words.length - 1) {
        result.push(words[i] + ' ' + words[i + 1])
        i++ // Skip next word
      } else {
        result.push(words[i])
      }
    }

    // Assign firstName, lastName, and middleName
    if (result.length === 2) {
      ;[firstName, lastName] = result
    } else if (result.length > 2) {
      firstName = result[0]
      lastName = result[result.length - 1]
      middleName = result.slice(1, -1).join(' ')
    }

    return {
      salutations: salutationArray.join(' ') || null,
      firstName: firstName || null,
      lastName: lastName || null,
      middleName: middleName || null
    }
  }

  ngOnDestroy(): void {
    if (this.basicInput?.inputElement?.nativeElement) {
      const inputElement = this.basicInput.inputElement.nativeElement
      inputElement.removeEventListener('keydown', this._boundOnKeydown)
      inputElement.removeEventListener('focus', this._boundOnFocus)
    }
    this._destroy$.next()
    this._destroy$.complete()
  }
}
